import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  message,
  Row,
  Col,
  Typography,
  Divider,
  Alert,
  Steps,
  Card,
  Checkbox,
  Tag,
  Space,
  List,
  Tooltip
} from 'antd';
import {
  CalendarOutlined,
  DollarOutlined,
  CreditCardOutlined,
  CheckOutlined,
  ArrowRightOutlined,
  SwapOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import PlanSelector from './PlanSelector';
import { formatSubscriptionAmount } from '../subscriptionFormatters';
import './SubscriptionRenewalEnhanced.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Types TypeScript
interface Plan {
  id: number;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_cycle: 'monthly' | 'quarterly' | 'annual';
  features: string[];
  max_users: number | null;
  max_companies: number;
  storage_limit_gb: number | null;
  is_trial: boolean;
  trial_duration_days: number;
  is_active: boolean;
  sort_order: number;
}

interface Company {
  id: number;
  name: string;
  short_name: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  trial_ends_at: string | null;
  licence_expire_on: string | null;
  subscription_plan_id: number | null;
  package_type: 'monthly' | 'quarterly' | 'semiannual' | 'annual' | null;
  total_users: number;
  created_at: string;
  plan_name: string | null;
  plan_price: number | null;
  plan_currency: string | null;
  plan_billing_cycle: string | null;
  subscription_status: 'active' | 'expired' | 'trial' | 'suspended';
  days_remaining: number | null;
}

interface SubscriptionRenewalEnhancedModalProps {
  visible: boolean;
  company: Company | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface WarehousePricingQuote {
  billable_warehouse_count: number;
  warehouse_unit_price: number;
  monthly_unit_price: number;
  gross_amount: number;
  discount_months: number;
  discount_amount: number;
  total_amount: number;
  currency: string;
  rate_source: 'catalog' | 'negotiated';
}

const SubscriptionRenewalEnhancedModal: React.FC<SubscriptionRenewalEnhancedModalProps> = ({
  visible,
  company,
  onSuccess,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [pricingQuote, setPricingQuote] = useState<WarehousePricingQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [collectionMode, setCollectionMode] = useState<'prepaid' | 'on_account'>('prepaid');
  const [planSelectionMode, setPlanSelectionMode] = useState<'keep' | 'change'>('keep');

  // État local pour persister les valeurs du formulaire
  const [formValues, setFormValues] = useState({
    duration_months: 12,
    payment_method: 'admin_renewal',
    payment_confirmed: false,
    notes: ''
  });
  const quoteRequestRef = React.useRef<AbortController | null>(null);

  // Charger les plans disponibles
  const fetchAvailablePlans = async () => {
    try {
      const response = await fetch('/api/subscription/plans?status=active&limit=50', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setAvailablePlans(data.data.plans);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des plans:', error);
      message.error('Erreur lors du chargement des plans');
    }
  };

  const fetchPricingQuote = async (months: number) => {
    if (!company) return;
    quoteRequestRef.current?.abort();
    const requestController = new AbortController();
    quoteRequestRef.current = requestController;
    setQuoteLoading(true);
    try {
      const response = await fetch(
        `/api/subscription/companies/${company.id}/quote?duration_months=${months}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          signal: requestController.signal
        }
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Devis impossible');
      }
      setPricingQuote(data.data.quote);
      setTotalAmount(Number(data.data.quote.total_amount) || 0);
      if (data.data.collection_mode === 'on_account') {
        setCollectionMode('on_account');
        setFormValues(prev => ({ ...prev, payment_method: 'on_account' }));
        form.setFieldValue('payment_method', 'on_account');
      } else {
        setCollectionMode('prepaid');
        setFormValues(prev => ({
          ...prev,
          payment_method:
            prev.payment_method === 'on_account'
              ? 'admin_renewal'
              : prev.payment_method
        }));
        if (form.getFieldValue('payment_method') === 'on_account') {
          form.setFieldValue('payment_method', 'admin_renewal');
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setPricingQuote(null);
      setTotalAmount(0);
      message.error(error instanceof Error ? error.message : 'Devis impossible');
    } finally {
      if (quoteRequestRef.current === requestController) {
        quoteRequestRef.current = null;
        setQuoteLoading(false);
      }
    }
  };

  // Réinitialiser le modal à l'ouverture
  useEffect(() => {
    if (visible && company) {
      setCurrentStep(0);
      setSelectedPlan(null);
      setPlanSelectionMode('keep');
      setTotalAmount(0);
      setPricingQuote(null);
      setCollectionMode('prepaid');

      // Réinitialiser les valeurs du formulaire
      const initialValues = {
        duration_months: 12,
        payment_method: 'admin_renewal',
        payment_confirmed: false,
        notes: ''
      };
      setFormValues(initialValues);
      form.resetFields();
      form.setFieldsValue(initialValues);

      fetchAvailablePlans();
      fetchPricingQuote(12);
    }
    return () => {
      quoteRequestRef.current?.abort();
    };
    // fetchPricingQuote est volontairement déclenché uniquement à l'ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, company, form]);

  // Gérer la sélection du mode (garder ou changer)
  const handleModeChange = (mode: 'keep' | 'change') => {
    setPlanSelectionMode(mode);
    setSelectedPlan(null);
  };

  // Gérer la sélection d'un nouveau plan
  const handlePlanSelect = (plan: Plan) => {
    setSelectedPlan(plan);
  };

  // Gérer le changement de durée
  const handleDurationChange = (months: number) => {
    const validMonths = months || 1;
    setFormValues(prev => ({ ...prev, duration_months: validMonths }));
    fetchPricingQuote(validMonths);
  };

  // Gérer le changement de méthode de paiement
  const handlePaymentMethodChange = (method: string) => {
    setFormValues(prev => ({ ...prev, payment_method: method }));
  };

  // Gérer le changement de notes
  const handleNotesChange = (notes: string) => {
    setFormValues(prev => ({ ...prev, notes: notes || '' }));
  };

  // Valider et passer à l'étape suivante
  const handleNext = () => {
    if (currentStep === 0) {
      // Validation de l'étape 1 (sélection du plan)
      if (planSelectionMode === 'change' && !selectedPlan) {
        message.warning('Veuillez sélectionner un nouveau plan');
        return;
      }
    } else if (currentStep === 1) {
      // Validation de l'étape 2 (configuration)
      form.validateFields(
        collectionMode === 'on_account'
          ? ['duration_months', 'notes']
          : ['duration_months', 'payment_method', 'payment_confirmed', 'notes']
      ).then(() => {
        setCurrentStep(currentStep + 1);
      }).catch(() => {
        message.warning('Veuillez remplir tous les champs requis');
      });
      return;
    }

    setCurrentStep(currentStep + 1);
  };

  // Revenir à l'étape précédente
  const handlePrevious = () => {
    setCurrentStep(Math.max(0, currentStep - 1));
  };

  // Soumettre le renouvellement
  const handleSubmit = async () => {
    if (!company) return;

    setLoading(true);
    try {
      // Utiliser l'état local qui contient les vraies valeurs
      const payload = {
        duration_months: formValues.duration_months,
        payment_method: formValues.payment_method,
        payment_confirmed:
          collectionMode === 'prepaid' && formValues.payment_confirmed,
        notes: formValues.notes || '',
        ...(planSelectionMode === 'change' && selectedPlan && {
          subscription_plan_id: selectedPlan.id
        })
      };

      const response = await fetch(`/api/subscription/companies/${company.id}/renew`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        const action = planSelectionMode === 'change' && selectedPlan
          ? `Plan modifié vers ${selectedPlan.name} et souscription renouvelée`
          : 'Souscription renouvelée';
        message.success(`${action} avec succès`);
        form.resetFields();
        onSuccess();
      } else {
        message.error(data.message || 'Erreur lors du renouvellement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Définition des étapes
  const steps = [
    {
      title: 'Plan',
      description: 'Choisir le plan',
      icon: <SwapOutlined />
    },
    {
      title: 'Configuration',
      description: 'Durée et paiement',
      icon: <CalendarOutlined />
    },
    {
      title: 'Confirmation',
      description: 'Valider les détails',
      icon: <CheckOutlined />
    }
  ];

  if (!company) return null;

  return (
    <Modal
      title={
        <div className="flex items-center">
          <CalendarOutlined className="mr-2 text-primary" />
          Renouveler / Modifier la souscription
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={900}
      destroyOnClose
      className="subscription-renewal-enhanced-modal"
    >
      {/* Informations sur l'entreprise */}
      <Alert
        message={
          <div>
            <Title level={5} className="mb-0">{company.name}</Title>
            <Text type="secondary">{company.email}</Text>
          </div>
        }
        description={
          <div className="mt-2">
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Plan actuel: </Text>
                <Tag color="blue">{company.plan_name || 'Aucun'}</Tag>
              </Col>
              <Col span={12}>
                <Text strong>Statut: </Text>
                <Tag color={company.subscription_status === 'active' ? 'green' :
                          company.subscription_status === 'trial' ? 'orange' : 'red'}>
                  {company.subscription_status}
                </Tag>
              </Col>
            </Row>
            <div className="mt-2">
              <Text strong>Facturation : </Text>
              <Text>par Warehouse actif, indépendamment du plan</Text>
            </div>
          </div>
        }
        type="info"
        className="mb-4"
      />

      {/* Indicateur d'étapes */}
      <Steps current={currentStep} className="mb-6" items={steps} />

      {/* Étape 1: Sélection du plan */}
      {currentStep === 0 && (
        <div>
          <Title level={4}>
            <SwapOutlined className="mr-2" />
            Choisir un plan d'abonnement
          </Title>

          {/* Options de sélection */}
          <div className="mb-4">
            <Space direction="vertical" className="w-full">
              <Card
                className={`cursor-pointer transition-all ${planSelectionMode === 'keep' ? 'border-primary bg-blue-50' : 'border-gray-200'}`}
                onClick={() => handleModeChange('keep')}
                size="small"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong>Garder le plan actuel</Text>
                    <div className="text-sm text-gray-500">
                      {company.plan_name} — fonctionnalités et limites conservées
                    </div>
                  </div>
                  {planSelectionMode === 'keep' && <CheckOutlined className="text-primary" />}
                </div>
              </Card>

              <Card
                className={`cursor-pointer transition-all ${planSelectionMode === 'change' ? 'border-primary bg-blue-50' : 'border-gray-200'}`}
                onClick={() => handleModeChange('change')}
                size="small"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <Text strong>Changer de plan</Text>
                    <div className="text-sm text-gray-500">
                      Sélectionner un nouveau plan d'abonnement
                    </div>
                  </div>
                  {planSelectionMode === 'change' && <CheckOutlined className="text-primary" />}
                </div>
              </Card>
            </Space>
          </div>

          {/* Sélecteur de plans (si mode changement) */}
          {planSelectionMode === 'change' && (
            <>
              <Divider>Plans disponibles</Divider>

              {availablePlans.length > 0 ? (
                <PlanSelector
                  plans={availablePlans.filter(p => p.id !== company.subscription_plan_id)}
                  selectedPlanId={selectedPlan?.id}
                  onSelect={handlePlanSelect}
                  showFeatures={true}
                  layout="horizontal"
                  highlightRecommended={true}
                />
              ) : (
                <Alert
                  message="Aucun plan disponible"
                  description="Aucun autre plan n'est actuellement disponible pour cette entreprise."
                  type="warning"
                  showIcon
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Étape 2: Configuration */}
      {currentStep === 1 && (
        <div>
          <Title level={4}>
            <CalendarOutlined className="mr-2" />
            Configuration du renouvellement
          </Title>

          {/* Résumé du plan choisi */}
          <Alert
            message={
              planSelectionMode === 'change' && selectedPlan
                ? `✨ Nouveau plan sélectionné: ${selectedPlan.name}`
                : `📋 Plan actuel conservé: ${company.plan_name}`
            }
            type={planSelectionMode === 'change' ? "success" : "info"}
            className="mb-4"
            showIcon
          />

          <Form
            form={form}
            layout="vertical"
            initialValues={formValues}
            onValuesChange={(changedValues, allValues) => {
              // Synchroniser l'état local avec les changements du formulaire
              if (changedValues.duration_months !== undefined) {
                handleDurationChange(changedValues.duration_months);
              }
              if (changedValues.payment_method !== undefined) {
                handlePaymentMethodChange(changedValues.payment_method);
              }
              if (changedValues.notes !== undefined) {
                handleNotesChange(changedValues.notes);
              }
              // Mettre à jour l'état local avec toutes les valeurs
              setFormValues(prev => ({ ...prev, ...changedValues }));
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="duration_months"
                  label="Durée du renouvellement"
                  rules={[
                    { required: true, message: 'Durée requise' },
                    { type: 'number', min: 1, max: 36, message: 'Entre 1 et 36 mois' }
                  ]}
                >
                  <InputNumber
                    min={1}
                    max={36}
                    suffix="mois"
                    className="w-full"
                    placeholder="Nombre de mois"
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                {collectionMode === 'on_account' ? (
                  <Form.Item label="Mode de règlement">
                    <Input value="À compte — facture à recouvrer" disabled />
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="payment_method"
                    label="Méthode de paiement"
                    rules={[{ required: true, message: 'Méthode de paiement requise' }]}
                  >
                    <Select
                      placeholder="Sélectionner une méthode"
                      suffixIcon={<CreditCardOutlined />}
                    >
                      <Option value="admin_renewal">Renouvellement administrateur</Option>
                      <Option value="bank_transfer">Virement bancaire</Option>
                      <Option value="cash">Espèces</Option>
                      <Option value="check">Chèque</Option>
                      <Option value="mobile_money">Mobile Money</Option>
                      <Option value="stripe">Carte bancaire (Stripe)</Option>
                    </Select>
                  </Form.Item>
                )}
              </Col>
            </Row>

            {collectionMode === 'prepaid' && (
              <Alert
                type="warning"
                showIcon
                message="Le renouvellement prépayé exige un paiement réellement reçu"
                description={
                  <Form.Item
                    name="payment_confirmed"
                    valuePropName="checked"
                    rules={[
                      {
                        validator: (_, checked) =>
                          checked
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error('Confirmez la réception du paiement'),
                              ),
                      },
                    ]}
                    style={{ margin: '8px 0 0' }}
                  >
                    <Checkbox>
                      Je confirme que le paiement a été reçu et peut être enregistré.
                    </Checkbox>
                  </Form.Item>
                }
                className="mb-4"
              />
            )}

            <Form.Item
              name="notes"
              label="Motif du renouvellement"
              rules={[
                { required: true, whitespace: true, message: 'Motif obligatoire' },
                { min: 3, max: 500, message: 'Entre 3 et 500 caractères' }
              ]}
            >
              <TextArea
                rows={3}
                placeholder="Référence du règlement ou justification de la décision..."
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Form>

          {/* Aperçu des prix */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <Title level={5} className="mb-3">
              <DollarOutlined className="mr-2" />
              Aperçu financier
            </Title>

            <Row gutter={[16, 8]}>
              <Col span={8}>
                <Text type="secondary">Tarif / Warehouse:</Text>
                <div className="text-lg font-semibold">
                  {formatSubscriptionAmount(
                    pricingQuote?.warehouse_unit_price,
                    pricingQuote?.currency || 'XOF'
                  )}
                </div>
              </Col>

              <Col span={8}>
                <Text type="secondary">Warehouses facturés:</Text>
                <div className="text-lg font-semibold">
                  {pricingQuote?.billable_warehouse_count ?? '—'}
                </div>
              </Col>

              <Col span={8}>
                <Text type="secondary">Montant total:</Text>
                <div className="text-xl font-bold text-primary">
                  {formatSubscriptionAmount(
                    totalAmount,
                    pricingQuote?.currency || 'XOF'
                  )}
                </div>
              </Col>
            </Row>
            {pricingQuote && pricingQuote.discount_amount > 0 && (
              <Text type="success">
                Remise annuelle : {formatSubscriptionAmount(
                  pricingQuote.discount_amount,
                  pricingQuote.currency
                )} ({pricingQuote.discount_months} mois offert)
              </Text>
            )}
            {quoteLoading && <Text type="secondary">Recalcul du devis…</Text>}
          </div>
        </div>
      )}

      {/* Étape 3: Confirmation */}
      {currentStep === 2 && (
        <div>
          <Title level={4}>
            <CheckOutlined className="mr-2" />
            Confirmation du renouvellement
          </Title>

          <Card className="mb-4">
            <List
              itemLayout="horizontal"
              dataSource={[
                {
                  title: 'Entreprise',
                  content: company.name
                },
                {
                  title: 'Plan',
                  content: (
                    <div>
                      {planSelectionMode === 'change' && selectedPlan ? (
                        <div>
                          <Text strong className="text-green-600">{selectedPlan.name} (nouveau)</Text>
                          <div className="text-sm text-gray-500">
                            Changement depuis: {company.plan_name}
                          </div>
                        </div>
                      ) : (
                        <Text strong>{company.plan_name} (actuel)</Text>
                      )}
                    </div>
                  )
                },
                {
                  title: 'Durée',
                  content: `${formValues.duration_months} mois`
                },
                {
                  title: 'Mode de règlement',
                  content: collectionMode === 'on_account'
                    ? 'À compte — créance non réglée'
                    : `${formValues.payment_method} — paiement confirmé manuellement`
                },
                {
                  title: 'Montant total',
                  content: (
                    <Text strong className="text-xl text-primary">
                      {formatSubscriptionAmount(
                        totalAmount,
                        pricingQuote?.currency || 'XOF'
                      )}
                    </Text>
                  )
                }
              ]}
              renderItem={(item) => (
                <List.Item>
                  <div className="flex justify-between w-full">
                    <Text type="secondary">{item.title}:</Text>
                    <div>{item.content}</div>
                  </div>
                </List.Item>
              )}
            />

            {formValues.notes && (
              <>
                <Divider />
                <div>
                  <Text type="secondary">Notes:</Text>
                  <div className="mt-1 p-2 bg-gray-50 rounded">
                    {formValues.notes}
                  </div>
                </div>
              </>
            )}
          </Card>

          {planSelectionMode === 'change' && selectedPlan && (
            <Alert
              message="Changement de plan"
              description={`Le plan sera changé de "${company.plan_name}" vers "${selectedPlan.name}" lors de ce renouvellement.`}
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              className="mb-4"
            />
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-6 pt-4 border-t">
        <div>
          {currentStep > 0 && (
            <Button onClick={handlePrevious} disabled={loading}>
              Précédent
            </Button>
          )}
        </div>

        <div className="space-x-2">
          <Button onClick={onCancel} disabled={loading}>
            Annuler
          </Button>

          {currentStep < steps.length - 1 ? (
            <Button type="primary" onClick={handleNext} disabled={loading}>
              Suivant
              <ArrowRightOutlined />
            </Button>
          ) : (
            <Button
              type="primary"
              loading={loading || quoteLoading}
              onClick={async () => {
                try {
                  // Validation finale avec les valeurs de l'état local
                  if (!formValues.duration_months || formValues.duration_months < 1 || formValues.duration_months > 36) {
                    message.error('Durée invalide (1-36 mois)');
                    return;
                  }
                  if (collectionMode !== 'on_account' && !formValues.payment_method) {
                    message.error('Méthode de paiement requise');
                    return;
                  }
                  if (collectionMode !== 'on_account' && !formValues.payment_confirmed) {
                    message.error('Confirmez que le paiement a réellement été reçu');
                    return;
                  }
                  if (!formValues.notes || formValues.notes.trim().length < 3) {
                    message.error('Le motif du renouvellement est obligatoire');
                    return;
                  }
                  if (!pricingQuote) {
                    message.error('Le devis Warehouse doit être calculé avant validation');
                    return;
                  }
                  await handleSubmit();
                } catch (error) {
                  console.error('Erreur validation:', error);
                  message.error('Erreur lors de la validation');
                }
              }}
              icon={<CheckOutlined />}
            >
              {collectionMode === 'on_account'
                ? 'Confirmer le renouvellement'
                : 'Enregistrer le paiement et renouveler'}
            </Button>
          )}
        </div>
      </div>

    </Modal>
  );
};

export default SubscriptionRenewalEnhancedModal;
